// Frame-time and per-section sampling for the map render loop.
//
// Sections are accumulated within a frame and rolled into a per-frame total when
// endFrame() is called, so a section that runs several times per frame reports the
// cost of the whole frame's worth of work rather than one call.
//
// Percentiles matter more than the mean here: the map's problems show up as
// occasional multi-hundred-millisecond stalls that a mean hides completely.

import { devtoolsParams } from "./devtools/params";

const SAMPLE_COUNT = 600; // 10 seconds at 60fps

function createRing() {
  return { values: new Float32Array(SAMPLE_COUNT), write: 0, filled: 0 };
}

function pushSample(ring, value) {
  ring.values[ring.write] = value;
  ring.write = (ring.write + 1) % SAMPLE_COUNT;
  if (ring.filled < SAMPLE_COUNT) ring.filled++;
}

// Scratch buffer reused by percentiles() so sampling doesn't allocate.
const sortScratch = new Float32Array(SAMPLE_COUNT);

function percentiles(ring) {
  const n = ring.filled;
  if (n === 0) return { p50: 0, p95: 0, p99: 0, max: 0, mean: 0 };

  let total = 0;
  for (let i = 0; i < n; i++) {
    const v = ring.values[i];
    sortScratch[i] = v;
    total += v;
  }

  const sorted = sortScratch.subarray(0, n);
  sorted.sort();

  const at = (q) => sorted[Math.min(n - 1, Math.floor(q * n))];

  return {
    p50: at(0.5),
    p95: at(0.95),
    p99: at(0.99),
    max: sorted[n - 1],
    mean: total / n,
  };
}

// Real wall-clock between successive frames. This is what fps actually is, and
// unlike the callback duration it includes websocket work, style recalculation
// and paint, which all happen outside the rAF callback.
const frameRing = createRing();

// Time spent inside the rAF callback itself. The gap between this and frameRing
// is the browser's own layout/paint cost plus off-frame message processing.
const callbackRing = createRing();

const sectionRings = {}; // key -> ring of per-frame totals
const sectionAccum = {}; // key -> ms accumulated so far this frame
const counters = {}; // name -> () => number

let enabled = false;
let frameCount = 0;
let lastFrameStart = 0;
let lastFrameEnd = 0;

// Sections opened with begin() but not yet closed. A plain object keeps this
// allocation-free for the handful of concurrent sections the loop actually uses.
const openSections = {};

function begin(key) {
  if (!enabled) return;
  openSections[key] = performance.now();
}

function end(key) {
  if (!enabled) return;
  const started = openSections[key];
  if (started === undefined) return;
  openSections[key] = undefined;
  accum(key, performance.now() - started);
}

function accum(key, ms) {
  if (!enabled) return;
  sectionAccum[key] = (sectionAccum[key] || 0) + ms;
}

function beginFrame() {
  if (!enabled) return;

  const now = performance.now();
  if (lastFrameStart) pushSample(frameRing, now - lastFrameStart);
  lastFrameStart = now;
}

function endFrame() {
  if (!enabled) return;

  frameCount++;
  pushSample(callbackRing, performance.now() - lastFrameStart);

  for (const key in sectionAccum) {
    let ring = sectionRings[key];
    if (!ring) ring = sectionRings[key] = createRing();
    pushSample(ring, sectionAccum[key]);
    sectionAccum[key] = 0;
  }
}

// Counters are pulled rather than pushed, so an expensive one (see elementCount
// in BorgMap) only costs anything when something actually reads the snapshot.
function counter(name, fn) {
  counters[name] = fn;
}

function snapshot() {
  const sections = {};
  for (const key in sectionRings) {
    sections[key] = percentiles(sectionRings[key]);
  }

  const counts = {};
  for (const name in counters) {
    try {
      counts[name] = counters[name]();
    } catch (e) {
      counts[name] = NaN;
    }
  }

  return {
    frame: percentiles(frameRing),
    callback: percentiles(callbackRing),
    frames: frameCount,
    sections,
    counters: counts,
  };
}

function reset() {
  frameRing.write = frameRing.filled = 0;
  callbackRing.write = callbackRing.filled = 0;
  lastFrameStart = 0;
  frameCount = 0;
  for (const key in sectionRings) delete sectionRings[key];
  for (const key in sectionAccum) delete sectionAccum[key];
}

function setEnabled(on) {
  enabled = !!on;
  if (!enabled) reset();
}

const perf = {
  begin,
  end,
  accum,
  beginFrame,
  endFrame,
  counter,
  snapshot,
  reset,
  setEnabled,
  get enabled() {
    return enabled;
  },
};

// Enabled by ?perf=1 so a session can be profiled without a rebuild. Driving the
// map from a tape or a synthetic estate implies profiling, so those enable it too.
if (typeof window !== "undefined") {
  const params = devtoolsParams();
  setEnabled(
    params.get("perf") === "1" || params.has("tape") || params.has("synth")
  );
  window.perf = perf;
}

export default perf;
